<template>
  <WebLayout
    title="Marketplace"
    description="Descubre tiendas y productos de todos los negocios en Realgestion Marketplace."
    url="https://realgestion.lat/marketplace"
    class="bg-white font-sans selection:bg-orange-500/30"
  >
    <Head :title="`Marketplace — Realgestion`" />

    <!-- ═══════════════════════════════════════════
         HERO
    ═══════════════════════════════════════════ -->
    <div class="relative overflow-hidden bg-gray-50 pt-20 pb-16">
      <!-- Background orbs -->
      <div class="absolute top-0 left-1/2 w-full -translate-x-1/2 h-full overflow-hidden pointer-events-none">
        <div class="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-orange-400/10 blur-[120px]" />
        <div class="absolute top-[20%] -right-[10%] w-[40%] h-[60%] rounded-full bg-rose-400/10 blur-[120px]" />
        <div class="absolute -bottom-[20%] left-[20%] w-[60%] h-[50%] rounded-full bg-indigo-400/10 blur-[120px]" />
        <div class="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjEiIGZpbGw9InJnYmEoMCwwLDAsMC4wMykiLz48L3N2Zz4=')] [mask-image:linear-gradient(to_bottom,white,transparent)]" />
      </div>

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div class="text-center max-w-3xl mx-auto">
          <!-- Badge -->
          <div class="inline-flex items-center px-4 py-1.5 rounded-full bg-orange-100 border border-orange-200 text-orange-600 text-sm font-medium mb-6">
            <span class="flex h-2 w-2 rounded-full bg-orange-500 mr-2 animate-pulse" />
            Marketplace Realgestion
          </div>

          <h1 class="text-5xl tracking-tight font-extrabold sm:text-6xl md:text-7xl lg:leading-[1.1]">
            <span class="block text-slate-900">Descubre</span>
            <span class="block bg-clip-text text-transparent bg-gradient-to-r from-orange-500 via-rose-500 to-orange-600 pb-2">Tiendas & Productos</span>
          </h1>

          <p class="mt-6 text-lg text-slate-600 sm:text-xl max-w-2xl mx-auto font-light leading-relaxed">
            Explora el ecosistema de negocios que confían en Realgestion. Encuentra productos, conecta con tiendas y compra directamente.
          </p>

          <!-- Stats -->
          <div class="mt-10 grid grid-cols-3 gap-4 border-t border-gray-200 pt-8 max-w-md mx-auto text-center">
            <div>
              <p class="text-3xl font-bold text-slate-900 mb-1">{{ totalAccounts }}</p>
              <p class="text-xs text-slate-500 font-medium uppercase tracking-wide">Tiendas</p>
            </div>
            <div>
              <p class="text-3xl font-bold text-slate-900 mb-1">{{ totalProducts }}<span class="text-orange-500">+</span></p>
              <p class="text-xs text-slate-500 font-medium uppercase tracking-wide">Productos</p>
            </div>
            <div>
              <p class="text-3xl font-bold text-slate-900 mb-1">24<span class="text-orange-500">/</span>7</p>
              <p class="text-xs text-slate-500 font-medium uppercase tracking-wide">Disponible</p>
            </div>
          </div>

          <!-- Search -->
          <div class="mt-10 max-w-xl mx-auto">
            <div class="relative">
              <i class="material-icons-outlined absolute left-5 top-1/2/2 text-gray-400">search</i>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Buscar tiendas, productos o categorías..."
                class="w-full pl-12 pr-36 py-4 bg-white rounded-2xl text-base border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400 shadow-lg shadow-gray-200/50 transition-all"
                @keyup.enter="doSearch"
              />
              <button
                class="absolute right-2 top-1/2 -translate-y-1/2 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-semibold px-6 py-2.5 rounded-xl hover:from-orange-400 hover:to-orange-500 transition-all shadow-md"
                @click="doSearch"
              >
                Buscar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════
         ECOMMERCE SECTION
    ═══════════════════════════════════════════ -->
    <div class="bg-gray-50 min-h-screen pb-24 border-t border-gray-100">
      <!-- Categorías de Negocios (Sticky) -->
      <div v-if="businessTypes.length > 0" class="bg-white border-b border-gray-200 sticky top-16 z-30 shadow-sm">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div class="flex items-center gap-2 overflow-x-auto no-scrollbar">
            <button
              :class="[
                'px-5 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-200',
                !selectedType
                  ? 'bg-slate-900 text-white'
                  : 'bg-gray-100 text-slate-600 hover:bg-gray-200'
              ]"
              @click="selectedType = ''"
            >
              Todos los departamentos
            </button>
            <button
              v-for="type in businessTypes"
              :key="type"
              :class="[
                'px-5 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200',
                selectedType === type
                  ? 'bg-slate-900 text-white'
                  : 'bg-gray-100 text-slate-600 hover:bg-gray-200'
              ]"
              @click="selectedType = type"
            >
              {{ type }}
            </button>
          </div>
        </div>
      </div>

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
        <!-- Active search indicator -->
        <div v-if="filters.search" class="flex items-center gap-3 mb-8">
          <span class="text-slate-500 text-sm">Resultados para:</span>
          <span class="bg-orange-100 text-orange-700 text-sm font-semibold px-3 py-1 rounded-full border border-orange-200">{{ filters.search }}</span>
          <button class="text-slate-400 hover:text-orange-600 text-sm transition-colors" @click="searchQuery = ''; doSearch()">
            <i class="material-icons-outlined mr-1">close</i> Limpiar
          </button>
        </div>

        <template v-if="filteredAccounts.length > 0">
          <!-- Proveedores / Tiendas Oficiales (Horizontal Scroll) -->
          <div class="mb-12">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-2xl font-bold text-slate-900">Tiendas Destacadas</h2>
            </div>
            
            <div class="flex gap-4 overflow-x-auto pb-4 no-scrollbar snap-x">
              <a
                v-for="account in filteredAccounts"
                :key="account.id"
                :href="`/catalogo/${account.username || account.id}`"
                class="snap-start flex-shrink-0 w-64 bg-white border border-gray-200 rounded-2xl p-5 hover:border-orange-500 hover:shadow-lg transition-all duration-300 group"
              >
                <div class="flex items-center gap-4 mb-4">
                  <div class="w-14 h-14 rounded-full overflow-hidden bg-gray-100 border border-gray-200 flex-shrink-0">
                    <img v-if="account.logo_url" :src="account.logo_url" class="w-full h-full object-cover" />
                    <div v-else class="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-100 to-rose-100 text-orange-500 font-bold text-xl">
                      {{ account.name.charAt(0).toUpperCase() }}
                    </div>
                  </div>
                  <div class="overflow-hidden">
                    <h3 class="font-bold text-slate-900 truncate group-hover:text-orange-600 transition-colors">{{ account.name }}</h3>
                    <p class="text-xs text-slate-500 truncate">{{ account.business_type || 'Tienda' }}</p>
                  </div>
                </div>
                <div class="flex items-center justify-between mt-auto">
                  <span class="text-xs font-semibold text-slate-400"><i class="material-icons-outlined text-slate-300 mr-1">inventory_2</i> {{ account.products.length }} prod.</span>
                  <span class="text-orange-500 text-xs font-bold group-hover:translate-x-1 transition-transform">Visitar <i class="material-icons-outlined">arrow_forward</i></span>
                </div>
              </a>
            </div>
          </div>

          <!-- Global Product Grid -->
          <div>
            <h2 class="text-2xl font-bold text-slate-900 mb-6">Descubre Productos</h2>
            
            <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
              <a
                v-for="product in allProducts"
                :key="product.id"
                :href="`/catalogo/${product.store.username || product.store.id}`"
                class="group flex flex-col bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-orange-500 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300"
              >
                <!-- Image -->
                <div class="aspect-square bg-gray-100 relative overflow-hidden">
                  <img
                    v-if="product.photo_url"
                    :src="product.photo_url"
                    :alt="product.name"
                    class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <img
                    v-else
                    src="https://placehold.co/600x600/f8fafc/cbd5e1?text=Sin+Imagen"
                    alt="Sin imagen"
                    class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  
                  <!-- Hover overlay (Quick View concept) -->
                  <div class="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <span class="bg-white text-slate-900 text-xs font-bold px-4 py-2 rounded-full shadow-lg transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                      Ver en tienda
                    </span>
                  </div>
                </div>

                <!-- Info -->
                <div class="p-4 flex flex-col flex-grow">
                  <!-- Store context -->
                  <div class="flex items-center gap-1.5 mb-2">
                    <img v-if="product.store.logo_url" :src="product.store.logo_url" class="w-4 h-4 rounded-full object-cover" />
                    <i v-else class="fas fa-store text-slate-400 text-[10px]" />
                    <span class="text-[11px] text-slate-500 font-medium truncate hover:text-orange-600 transition-colors">
                      {{ product.store.name }}
                    </span>
                  </div>

                  <h3 class="text-sm font-medium text-slate-900 line-clamp-2 leading-snug mb-1 group-hover:text-orange-600 transition-colors">
                    {{ product.name }}
                  </h3>
                  
                  <div class="mt-auto pt-3 flex items-end justify-between">
                    <div>
                      <p v-if="product.price" class="text-lg font-extrabold text-slate-900">
                        ${{ parseFloat(product.price).toFixed(2) }}
                      </p>
                      <p v-else class="text-sm text-slate-400 italic">Consultar precio</p>
                    </div>
                    <button class="w-8 h-8 rounded-full bg-gray-50 text-slate-400 flex items-center justify-center group-hover:bg-orange-500 group-hover:text-white transition-colors border border-gray-100 group-hover:border-orange-500">
                      <i class="material-icons-outlined text-xs">shopping_bag</i>
                    </button>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </template>

        <!-- Empty state -->
        <div v-else class="text-center py-24 bg-white rounded-3xl border border-gray-100 shadow-sm mt-8">
          <div class="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gray-50 border border-gray-100 mb-6">
            <i class="material-icons-outlined text-gray-400 text-4xl">search</i>
          </div>
          <h3 class="text-2xl font-bold text-slate-800 mb-3">No encontramos resultados</h3>
          <p class="text-slate-500 text-base mb-8 max-w-md mx-auto">
            {{ filters.search ? `No hay tiendas o productos que coincidan con "${filters.search}"` : 'Aún no hay proveedores disponibles en esta categoría.' }}
          </p>
          <button
            v-if="filters.search || selectedType"
            class="inline-flex items-center gap-2 bg-slate-900 text-white font-semibold px-8 py-4 rounded-xl hover:bg-slate-800 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
            @click="selectedType = ''; searchQuery = ''; doSearch()"
          >
            <i class="material-icons-outlined">undo</i> Ver todo el catálogo
          </button>
        </div>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════
         CTA BANNER
    ═══════════════════════════════════════════ -->
    <div class="relative py-24 overflow-hidden border-t border-gray-100">
      <div class="absolute inset-0 bg-gradient-to-br from-orange-500 via-orange-600 to-rose-600 pointer-events-none" />
      <div class="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4xKSIvPjwvc3ZnPg==')] pointer-events-none opacity-60" />

      <div class="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div class="inline-flex items-center px-4 py-1.5 rounded-full bg-white/20 border border-white/30 text-white text-sm font-semibold mb-6 backdrop-blur-sm">
          <i class="material-icons-outlined mr-2 text-orange-200">rocket_launch</i>
          ¿Tienes un negocio?
        </div>
        <h2 class="text-4xl sm:text-5xl font-extrabold text-white tracking-tight mb-6 leading-tight">
          Publica tu tienda<br />
          <span class="text-orange-200">en el Marketplace</span>
        </h2>
        <p class="text-lg text-orange-100 font-light mb-10 max-w-xl mx-auto">
          Regístrate en Realgestion, carga tus productos y tu tienda aparecerá automáticamente en el marketplace. <strong class="text-white font-semibold">Sin costo adicional.</strong>
        </p>

        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <Link href="/register" class="group inline-flex items-center justify-center px-8 py-4 rounded-xl bg-white text-orange-600 font-bold text-base shadow-[0_4px_20px_rgba(0,0,0,0.2)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.3)] hover:-translate-y-0.5 transition-all duration-300">
            <i class="material-icons-outlined mr-2 group-hover:rotate-12 transition-transform duration-300">storefront</i>
            Crear mi tienda gratis
          </Link>
          <Link href="/" class="inline-flex items-center justify-center px-8 py-4 rounded-xl border-2 border-white/50 text-white font-semibold text-base hover:bg-white/10 hover:border-white transition-all duration-300 backdrop-blur-sm">
            <i class="material-icons-outlined mr-2">info</i>
            Conocer más
          </Link>
        </div>
      </div>
    </div>
  </WebLayout>
</template>

<script>
import { Link, Head } from '@inertiajs/inertia-vue3'
import { Inertia } from '@inertiajs/inertia'
import WebLayout from '@/Layouts/WebLayout.vue'

export default {
  components: {
    Link,
    Head,
    WebLayout,
  },
  props: {
    accounts: {
      type: Array,
      default: () => [],
    },
    totalAccounts: {
      type: Number,
      default: 0,
    },
    totalProducts: {
      type: Number,
      default: 0,
    },
    businessTypes: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      default: () => ({ search: '' }),
    },
  },
  data() {
    return {
      searchQuery: this.filters.search || '',
      selectedType: '',
    }
  },
  computed: {
    filteredAccounts() {
      const marketplaceAccounts = this.accounts.filter(a => a.in_marketplace)
      if (!this.selectedType) return marketplaceAccounts
      return marketplaceAccounts.filter(a => a.business_type === this.selectedType)
    },
    allProducts() {
      let products = []
      this.filteredAccounts.forEach(account => {
        account.products.forEach(product => {
          products.push({
            ...product,
            store: {
              id: account.id,
              name: account.name,
              username: account.username,
              logo_url: account.logo_url,
            },
          })
        })
      })
      // Mezclar aleatoriamente de forma determinística simple o por id
      return products.sort((a, b) => b.id - a.id)
    },
  },
  methods: {
    doSearch() {
      Inertia.get('/marketplace', { search: this.searchQuery }, { preserveState: true })
    },
    truncate(str, len) {
      if (!str) return ''
      return str.length > len ? str.substring(0, len) + '...' : str
    },
    planLabel(plan) {
      const labels = {
        free: 'Free',
        entrepreneur: 'Prime',
        sme: 'Pro',
        enterprise: 'Enterprise',
      }
      return labels[plan] || 'Free'
    },
    planBadgeClass(plan) {
      const classes = {
        free: 'bg-gray-100 text-gray-500 border border-gray-200',
        entrepreneur: 'bg-gradient-to-r from-orange-500 to-rose-500 text-white',
        sme: 'bg-gradient-to-r from-violet-500 to-indigo-500 text-white',
        enterprise: 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white',
      }
      return classes[plan] || classes.free
    },
  },
}
</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar { display: none; }
.no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
